import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFitStore } from '../store/useFitStore';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { describeBilling, billingToneColor } from '../lib/billing';

/* ============================================================
   Subscription — the client's access + billing hub. Two separate
   money streams, kept honest:
   1) App access — free during the beta (mirrors mobile's non-iOS
      behavior); activating just unlocks the dashboard, no card.
   2) Coaching fees — what the client pays their COACH, via the
      coach's Stripe Checkout link (webhook-synced client_billing).
   No fake payment theater: nothing here pretends to charge a card.
============================================================ */

const Subscription: React.FC = () => {
  const { user, clientBilling, processPayment, showToast } = useFitStore();
  const navigate = useNavigate();
  const [activating, setActivating] = useState(false);

  const billing = user ? clientBilling.find((b) => b.clientId === user.id) || null : null;
  const display = describeBilling(billing);
  const payable = billing?.hostedInvoiceUrl && (billing.status === 'checkout_created' || billing.status === 'past_due');
  const locked = user?.subscriptionStatus !== 'active';

  const activate = async () => {
    setActivating(true);
    try {
      await processPayment('beta');
      showToast('You’re in — welcome to FitFlex');
      navigate('/client');
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="main-content" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <header style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
        <h1 className="text-accent" style={{ fontSize: '2.5rem', fontWeight: 900 }}>
          {locked ? 'Ready to Start?' : 'Membership'}
        </h1>
        <p className="text-muted">
          {locked ? 'Activate your account to open your training dashboard.' : 'Your access and coaching billing.'}
        </p>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
        {/* App access */}
        <Card variant="glass" padding="var(--spacing-lg)" style={{ border: '1px solid var(--accent-primary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-md)' }}>
            <div>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 800 }}>FitFlex App Access</h3>
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                Workouts, progress tracking, nutrition, and coach chat.
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '1.8rem', fontWeight: 900 }}>Free</span>
              <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>during beta</p>
            </div>
          </div>
          {locked ? (
            <Button fullWidth onClick={activate} isLoading={activating}>
              CONTINUE — FREE DURING BETA
            </Button>
          ) : (
            <p style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--status-green)' }}>✓ Active</p>
          )}
        </Card>

        {/* Coaching fees — only when the coach has set up billing */}
        {billing && (
          <Card variant="glass" padding="var(--spacing-lg)">
            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '6px' }}>Coaching</h3>
            <p style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: payable ? 'var(--spacing-md)' : 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: billingToneColor(display.tone), flexShrink: 0 }} />
              <span className="text-muted">{display.text}</span>
            </p>
            {payable && (
              <a href={billing.hostedInvoiceUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <Button fullWidth variant="secondary">
                  {billing.status === 'past_due' ? 'PAY OUTSTANDING INVOICE' : 'COMPLETE PAYMENT'}
                </Button>
              </a>
            )}
          </Card>
        )}
      </div>

      <div style={{ marginTop: 'var(--spacing-xl)', textAlign: 'center' }}>
        <p className="text-muted" style={{ fontSize: '0.75rem' }}>
          Coaching payments are handled by your coach through <strong>Stripe</strong> — card details never touch FitFlex.
        </p>
      </div>
    </div>
  );
};

export default Subscription;
